<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash-es';
import { useApi } from '@/api';
import { useEventBus } from '@/setup/eventBus';
import { useError } from '@/composables/useError';
import UploadFile from '@/components/UploadFile.vue';
import { isErrorResponse, type EmitsToListeners, type FormErrors, type UploadedFile } from '@/types/core';
import type { OverrideFields } from '@/types/util';
import type {
  GetAffiliateOffersResponse,
  GetAffiliatesData,
  GetAffiliatesResponse,
  PostPlayersMassAttachToAffiliateData,
  PostPlayersMassAttachToAffiliateResponse,
  PostPlayersMassDetachFromAffiliateData,
  PostPlayersMassDetachFromAffiliateResponse,
  PostPlayersMassMarkAsVirtualData,
  PostPlayersMassMarkAsVirtualResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type TransferAction =
  | 'detach'
  | 'detach_partner'
  | 'attach'
  | 'attach_partner'
  | 'virtual';
type UploadType = 'file' | 'text';

type MassAttachBody = PostPlayersMassAttachToAffiliateData['body'];
type MassDetachBody = PostPlayersMassDetachFromAffiliateData['body'];
type MassVirtualBody = PostPlayersMassMarkAsVirtualData['body'];

type TransferUploadFields = OverrideFields<
  Pick<MassDetachBody, 'file_ids' | 'site_player_ids'>,
  {
    file_ids?: string[];
    site_player_ids?: string;
  }
>;

interface Props {
  siteId?: string | null;
}

interface Emits {
  success: [];
  close: [];
}

export interface TransferPlayersParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}

type TransferMassResponse =
  | PostPlayersMassAttachToAffiliateResponse
  | PostPlayersMassDetachFromAffiliateResponse
  | PostPlayersMassMarkAsVirtualResponse;
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const eventBus = useEventBus();
const { t } = useI18n();
const errors = ref<FormErrors>({});
const { getError } = useError({ errors });
//#endregion

//#region state
const uploadType = ref<UploadType>('file');
const files = ref<UploadedFile[]>([]);
const playersIds = ref('');
const action = ref<TransferAction>('detach');
const affiliateId = ref<string | null>(null);
const affiliateOptions = ref<GetAffiliatesResponse['payload']>([]);
const offerId = ref<string | null>(null);
const affiliateOfferOptions = ref<GetAffiliateOffersResponse['payload']>([]);
const searchQuery = ref('');
const limit = ref(33);
const selectLoading = ref(false);

const isVisible = ref(false);
const isLoading = ref(false);
//#endregion

//#region computed
const computedAffiliateLabel = computed(() => {
  const affiliate = affiliateId.value
    && affiliateOptions.value.find((el) => el.id === affiliateId.value)?.email;

  return affiliate || t('affiliates.transfer_popup.selected_affiliate');
});
//#endregion

//#region methods
function getUploadFields(): TransferUploadFields {
  if (uploadType.value === 'file') {
    const uploadId = files.value[0]?.uploadId;
    return uploadId ? { file_ids: [uploadId] } : {};
  }

  return { site_player_ids: playersIds.value };
}

function getDetachQuery(): MassDetachBody {
  return {
    site_id: props.siteId || '',
    ...getUploadFields(),
    ...(action.value === 'detach_partner' && {
      player_attached_to_affiliate_id: affiliateId.value,
    }),
  };
}

function getAttachQuery(): MassAttachBody {
  return {
    site_id: props.siteId || '',
    affiliate_id: affiliateId.value || '',
    affiliate_offer_id: offerId.value || '',
    overwrite_player_affiliate_attachment: action.value === 'attach',
    ...getUploadFields(),
  };
}

function getVirtualQuery(): MassVirtualBody {
  return {
    site_id: props.siteId || '',
    ...getUploadFields(),
  };
}

function changeAffiliate(id: string | null) {
  offerId.value = null;
  getAffiliateOffers(id);
}

function getAffiliateOffers(id: string | null) {
  if (!id) {
    affiliateOfferOptions.value = [];
    return;
  }

  isLoading.value = true;
  return api
    .getAffiliateOffers(id)
    .then((res) => {
      affiliateOfferOptions.value = props.siteId
        ? res.data.payload.filter((offer) => offer.brand_visibility === 'all'
          || (offer.brand_visibility === 'only_selected'
            && offer.visible_brands.some((brand) => brand.site_id === props.siteId)))
        : res.data.payload;
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function getAffiliates(val: string, nextLimit: number, isInit = true) {
  selectLoading.value = true;
  if (isInit) {
    isLoading.value = true;
  }
  const query: GetAffiliatesData['query'] = {
    search: val,
    limit: nextLimit,
    sort_column: 'email',
    sort_dir: 'asc',
  };

  return api
    .getAffiliates(query)
    .then((res) => {
      affiliateOptions.value = res.data.payload;
    })
    .finally(() => {
      isLoading.value = false;
      selectLoading.value = false;
    });
}

const getAffiliatesDebounced = debounce(getAffiliates, 800);

function searchMethod(v: string) {
  selectLoading.value = true;
  searchQuery.value = v;
  limit.value = 33;
  getAffiliatesDebounced(searchQuery.value, limit.value, false);
}

function open() {
  getAffiliates('', 33);
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
  emit('close');
}

async function handleSave() {
  errors.value = {};
  isLoading.value = true;

  let promise: Promise<{ data: TransferMassResponse }> | null = null;

  switch (action.value) {
  case 'detach':
  case 'detach_partner':
    promise = api.massDetachPlayerAffiliate(getDetachQuery());
    break;
  case 'attach':
  case 'attach_partner':
    promise = api.massAttachPlayerAffiliate(getAttachQuery());
    break;
  case 'virtual':
    promise = api.massMarkAsVirtual(getVirtualQuery());
    break;
  default:
    break;
  }

  if (promise) {
    try {
      const res = await promise;
      const { message, status } = res.data.payload;
      if (status === 'partially_changed' && message) {
        eventBus.$emit('notify-event', {
          type: 'info',
          text: message,
        });
      }

      emit('success');
      isVisible.value = false;
    } catch (e) {
      if (isErrorResponse(e)) {
        errors.value = e.data.errors;
      }
    } finally {
      isLoading.value = false;
    }
  }
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('affiliates.transfer_popup.transfer_players')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <CardTitle
          :title="$t('affiliates.transfer_popup.players_ids')"
        >
          <UiRadioGroup
            v-model="uploadType"
            :options="[
              {
                label: $t('crm.segments.manual.type.file'),
                value: 'file',
              },
              {
                label: $t('crm.segments.manual.type.manual'),
                value: 'text',
              }
            ]"
          />
        </CardTitle>
        <UploadFile
          v-if="uploadType === 'file'"
          v-model="files"
          :title="$t('affiliates.transfer_popup.upload_file')"
          accept=".csv,.txt"
          is-dragger
          :limit-file="1"
          :error="errors?.file_ids || errors?.player_attached_to_affiliate_id"
        />
        <UiTextarea
          v-if="uploadType === 'text'"
          v-model="playersIds"
          :label="$t('crm.segments.manual.text.title')"
          :placeholder="$t(`crm.segments.manual.text.placeholder`)"
          :rows="7"
          :error="errors?.site_player_ids || errors?.player_attached_to_affiliate_id"
        />
        <UiRadioGroup
          v-model="action"
          direction="col"
          :label="$t('affiliates.transfer_popup.action.title')"
          :options="[
            {
              label: $t('affiliates.transfer_popup.action.unpin'),
              value: 'detach',
            },
            {
              label: $t('affiliates.transfer_popup.action.unpin_from', { affiliateEmail: computedAffiliateLabel }),
              value: 'detach_partner',
            },
            {
              label: $t('affiliates.transfer_popup.action.pin_to', { affiliateEmail: computedAffiliateLabel }),
              value: 'attach',
            },
            {
              label: $t('affiliates.transfer_popup.action.pin_to_unpinned', { affiliateEmail: computedAffiliateLabel }),
              value: 'attach_partner',
            },
            {
              label: $t('affiliates.transfer_popup.mark_as_virtual'),
              value: 'virtual',
            }
          ]"
        />
        <UiSelectElement
          v-model="affiliateId"
          :label="$t('players.pinPlayer.aff')"
          :placeholder="$t('players.pinPlayer.placeholder_aff')"
          :list="affiliateOptions"
          :loading="selectLoading"
          :remote-method="searchMethod"
          remote
          filterable
          size="big"
          option-label-param="email"
          option-name-param="email"
          :error="getError('affiliate_id')"
          :disabled="['detach', 'virtual'].includes(action)"
          @change="changeAffiliate"
        />
        <UiSelectElement
          v-model="offerId"
          :placeholder="$t('players.pinPlayer.placeholder_offer')"
          :label="$t('players.pinPlayer.offer')"
          :disabled="['detach', 'detach_partner', 'virtual'].includes(action)"
          :list="affiliateOfferOptions"
          option-name-param="full_name"
          option-label-param="full_name"
          filterable
          size="big"
          :error="getError('affiliate_offer_id')"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          size="big"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          size="big"
          :disabled="isLoading"
          @click="handleSave"
        >
          {{ $t('affiliates.transfer_popup.transfer') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
